<template>
  <!-- Public routes (login, 404) render bare — no admin chrome. -->
  <ErrorBoundary v-if="route.meta.public">
    <router-view />
  </ErrorBoundary>

  <!-- Authenticated shell: the sidebar + header mount ONCE and persist across navigation; only the
       <router-view> content swaps. This keeps the menu scroll position, avoids the full-layout
       remount that felt like a page refresh, and is the standard persistent-layout SPA pattern.
       The ErrorBoundary keeps the shell alive and shows a recoverable fallback if a page crashes. -->
  <div v-else class="min-h-screen xl:flex">
    <SkipToContent />
    <AppSidebar v-if="!isFocusMode" :class="{ 'z-100': isMobileOpen }" />
    <Backdrop class="z-50" />

    <div
      class="min-w-0 flex-1 transition-all duration-300 ease-in-out z-100"
      :class="[isFocusMode ? 'lg:mr-0' : (isExpanded || isHovered ? 'lg:mr-[250px]' : 'lg:mr-[90px]')]"
    >
      <!-- Focus mode trades the chrome for working room: the header goes, the sidebar drops to its icon
           rail (hover still expands it), and AdminLayout widens the container. Nothing is removed — the
           toggle that turned it on is still on the page, and Esc-style escape is a click away. -->
      <AppHeader v-if="!isFocusMode" />
      <main id="main-content" class="min-h-[calc(100vh-4rem)] bg-gray-50 dark:bg-gray-900">
        <ErrorBoundary>
          <router-view />
        </ErrorBoundary>
      </main>
    </div>

    <!--
      ══ the way back, and why it lives HERE in the DOM ═══════════════════════════════════════════

      Focus mode is global, and until today its only toggle was on the one page that offered one. The
      header now enters it from all 119 — which turned a latent defect into a trap.

      This chip used to sit BEFORE the content wrapper. That wrapper is a flex item carrying `z-100`,
      and a flex item with a z-index makes a stacking context: the escape hatch out of focus mode was
      painted UNDERNEATH the page and could be neither seen nor clicked. The file said so in its own
      comment — «the escape hatch out of focus mode, at the bottom of the stack» — and nothing had ever
      reached it, because the only way into focus mode was a page that kept its own toggle on screen.

      Fixed by ORDER plus a NAMED layer rather than by a bigger number: `z-toast` is the top of §3.8's
      ladder and coming after the wrapper wins the tie against anything else sitting at it. The
      alternative was a fourth invented z-value for this panel to delete later.

      It leaves BOTH — the folded chrome and the browser's fullscreen — because the header's control
      enters both. Esc does the same, through `syncFullscreen()`; see `useFocusMode.js`.
    -->
    <button
      v-if="isFocusMode"
      type="button"
      class="fixed bottom-3 left-3 z-toast inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white/95 text-gray-600 shadow-sm backdrop-blur transition hover:bg-gray-50 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-gray-700 dark:bg-gray-900/95 dark:text-gray-200 dark:hover:bg-white/5"
      aria-label="خروج از حالت تمرکز و نمایش منوها"
      title="بازگرداندن منو و نوار بالا و خروج از تمام‌صفحه (کلید F)"
      data-testid="exit-focus-mode"
      @click="exitFocusMode"
    >
      <Minimize2 class="h-4 w-4" aria-hidden="true" />
    </button>

    <KeyboardHelpModal />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Minimize2 } from 'lucide-vue-next'
import AppSidebar from './AppSidebar.vue'
import AppHeader from './AppHeader.vue'
import Backdrop from './BackdropLayout.vue'
import ErrorBoundary from '@/components/common/ErrorBoundary.vue'
import SkipToContent from '@/components/common/SkipToContent.vue'
import KeyboardHelpModal from './KeyboardHelpModal.vue'
import { useSidebar } from '@/composables/useSidebar'
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
import { usePermission } from '@/composables/usePermission'
import { useSupportAttention } from '@/composables/useSupportAttention'
import { useFocusMode } from '@/composables/useFocusMode'
import { installPageScrollbar } from '@/utils/pageScrollbar'

const route = useRoute()
const router = useRouter()
const { isExpanded, isMobileOpen, isHovered } = useSidebar()
const { install } = useKeyboardShortcuts()
const { can } = usePermission()
const { startPolling, stopPolling } = useSupportAttention()
const { isFocusMode, syncFullscreen, exitBoth } = useFocusMode()

/**
 * The exact inverse of the header's button, and it has to be: that control enters BOTH — chrome folded
 * and browser fullscreen — so a chip that only gave the menu back would leave the operator in
 * fullscreen with a sidebar, which is neither of the two states anybody asked for.
 *
 * The pairing itself lives in the composable, so the header and this chip cannot drift apart about what
 * «both» means, and so Esc — which leaves fullscreen without telling the app — unwinds the same pair.
 */
const exitFocusMode = () => exitBoth()

let removePageScrollbar
watch(() => route.meta.public, isPublic => {
  removePageScrollbar?.()
  removePageScrollbar = isPublic ? undefined : installPageScrollbar()
}, { immediate: true })

onMounted(() => {
  install(router)
  if (can('support.read')) startPolling()
  document.addEventListener('fullscreenchange', syncFullscreen)
  syncFullscreen()
})

onUnmounted(() => {
  removePageScrollbar?.()
  stopPolling()
  document.removeEventListener('fullscreenchange', syncFullscreen)
})
</script>
